import {
  BaseEntity,
  ID,
  ListApiResponse,
  SingleApiResponse,
  unwrapApiErrorDetails
} from '@ianesthesie-kit/services';
import { GroupedList, GroupFunction } from '@/util';
import axios, { AxiosResponse } from 'axios';
import { MedicalOptionGroupCode } from '@/modules/surgicalEvent/option';
import { stringify } from 'query-string';
import { valueOfEnum } from '@ianesthesie-kit/core';
import {
  Emphasis,
  hasEmphasis,
  isMedicalComponent,
  SourceType
} from '@/modules/surgicalEvent/model';
import isEmpty from 'lodash/fp/isEmpty';

export type MedicalResponse<T, IsList extends boolean> = {
  data: IsList extends true ? T[] : T;
};
type ApiResponse<T, IsList extends boolean> = IsList extends true
  ? ListApiResponse<T>
  : SingleApiResponse<T>;

export abstract class BaseMedicalService<
  T extends BaseEntity,
  IsList extends boolean
> {
  protected readonly patientId?: ID;
  protected readonly url;

  protected unwrapErrorDetails = unwrapApiErrorDetails;

  protected constructor(urlSegment: string, patientId?: ID) {
    const seg = patientId
      ? `/${patientId}/surgical-event/active${urlSegment}`
      : `/surgical-event${urlSegment}`;
    this.url = `/bff/doctor/patient${seg}`;
    this.patientId = patientId;
  }

  protected static shouldCreateNewInstance<
    ST extends BaseEntity,
    L extends boolean
  >(instance: BaseMedicalService<ST, L>, patientId: ID): boolean {
    if (!patientId) {
      throw new Error('Patient id must be provided');
    }
    return !instance || instance.patientId !== patientId;
  }

  /**
   * Should the return the option group enum value that corresponds to each key.
   * This is used to extract option codes that are considered important (CRUCIAL Emphasis)
   * Optional operation
   */
  public getFieldOptionGroupMapping(): {
    [k in keyof T]?: MedicalOptionGroupCode;
  } {
    return {};
  }

  public create(newItem: Partial<T>): Promise<MedicalResponse<T, IsList>> {
    return this.upsert(newItem);
  }

  public update(item: Partial<T>): Promise<MedicalResponse<T, IsList>> {
    return this.upsert(item);
  }

  protected transformResponseEntity(data: any): T {
    if (hasEmphasis(data)) {
      data.emphasis = valueOfEnum(data.emphasis, Emphasis, {
        throwIfNotFound: true,
        caseSensitive: false
      })!;
    }
    if (isMedicalComponent(data)) {
      data.sourceType = valueOfEnum(data.sourceType, SourceType, {
        throwIfNotFound: true,
        caseSensitive: false
      })!;
    }
    return data as T;
  }

  protected transformToRequestBody(data: Partial<T>): Record<string, unknown> {
    return data;
  }

  protected getUrl(seg?: string): string;
  protected getUrl(params?: Record<string, any>): string;
  protected getUrl(seg: string, params: Record<string, any>): string;
  protected getUrl(
    seg?: string | Record<string, any>,
    params?: Record<string, any>
  ): string {
    if (isEmpty(seg) && isEmpty(params)) {
      return this.url;
    }

    if (typeof seg === 'object') {
      return `${this.url}?${stringify(seg)}`;
    }

    let url = this.url;
    if (seg) {
      url += `/${seg}`;
    }
    if (!isEmpty(params)) {
      url += `?${stringify(params)}`;
    }
    return url.replaceAll('//', '/');
  }

  public abstract fetch(
    params?: Record<string, any>
  ): Promise<MedicalResponse<T, IsList>>;
  public abstract upsert(
    item: Partial<T>,
    params?: Record<string, any>
  ): Promise<MedicalResponse<T, IsList>>;
  public abstract delete(item: T | ID): Promise<MedicalResponse<T, IsList>>;
}

export abstract class ListMedicalService<
  T extends BaseEntity
> extends BaseMedicalService<T, true> {
  fetch(params: Record<string, any> = {}): Promise<MedicalResponse<T, true>> {
    return axios
      .get(this.getUrl(params))
      .then((response: AxiosResponse<ApiResponse<T, true>>) => ({
        data: response.data.data.map((d) => this.transformResponseEntity(d))
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  fetchOne(
    entityId: ID,
    params: Record<string, any> = {}
  ): Promise<MedicalResponse<T, false>> {
    if (!entityId) {
      return Promise.reject(new Error('Id is undefined'));
    }
    return axios
      .get(this.getUrl(entityId, params))
      .then((response: AxiosResponse<ApiResponse<T, false>>) => ({
        data: this.transformResponseEntity(response.data.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  public fetchGrouped<K extends string, L extends boolean>(
    params?: Record<string, any>
  ): Promise<{
    data: GroupedList<T, K, L>;
  }> {
    const groupingFunction = this.getGroupingFunction();
    return this.fetch(params)
      .then((resp) => ({
        data: groupingFunction.apply(resp.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  upsert(
    item: Partial<T>,
    params?: Record<string, any>
  ): Promise<MedicalResponse<T, true>> {
    return axios
      .post(this.getUrl(params), this.transformToRequestBody(item))
      .then((response: AxiosResponse<ApiResponse<T, true>>) => ({
        data: response.data.data.map((d) => this.transformResponseEntity(d))
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  delete(item: ID | T): Promise<MedicalResponse<T, true>> {
    const itemId = typeof item === 'object' && 'id' in item ? item.id : item;
    return axios
      .delete(this.getUrl(itemId as string))
      .then((response: AxiosResponse<ApiResponse<T, true>>) => ({
        data: response.data.data.map((d) => this.transformResponseEntity(d))
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  protected getGroupingFunction<K extends string, L extends boolean>(): {
    apply: GroupFunction<T, K, L>;
  } {
    throw new Error('Unsupported operation');
  }
}

export abstract class SingleMedicalService<
  T extends BaseEntity
> extends BaseMedicalService<T, false> {
  fetch(params: Record<string, any> = {}): Promise<MedicalResponse<T, false>> {
    return axios
      .get(this.getUrl(params))
      .then((response: AxiosResponse<ApiResponse<T, false>>) => ({
        data: this.transformResponseEntity(response.data.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  upsert(
    item: Partial<T>,
    params: Record<string, any>
  ): Promise<MedicalResponse<T, false>> {
    return axios
      .post(this.getUrl(params), this.transformToRequestBody(item))
      .then((response: AxiosResponse<ApiResponse<T, false>>) => ({
        data: this.transformResponseEntity(response.data.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  delete(item: ID | T): Promise<MedicalResponse<T, false>> {
    const itemId = typeof item === 'object' && 'id' in item ? item.id : item;

    return axios
      .delete(this.getUrl(itemId as string))
      .then((response: AxiosResponse<ApiResponse<T, false>>) => ({
        data: this.transformResponseEntity(response.data.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  protected getGroupingFunction<K extends string, L extends boolean>(): {
    apply: GroupFunction<T, K, L>;
  } {
    throw new Error('Unsupported operation');
  }
}
